import { NgTemplateOutlet } from '@angular/common';
import type { AfterViewInit, OnInit, TemplateRef } from '@angular/core';
import {
  Component,
  effect,
  ElementRef,
  forwardRef,
  inject,
  input,
  model,
  Renderer2,
  signal,
  untracked,
  viewChild,
  viewChildren,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import type { FormValueControl } from '@angular/forms/signals';
import { TranslatePipe } from '@ngx-translate/core';
import { filter, fromEventPattern, skip } from 'rxjs';
import { ONYX_TRANSITION_DURATION } from '../../../constants';
import { ONYX_FOCUSABLE } from '../../../directives/onyx-auto-focus.directive';
import { OnyxTooltipDirective } from '../../../directives/onyx-tooltip.directive';
import type { OnyxOptional } from '../../../interfaces';
import { OnyxBaseFormControlComponent } from '../../../internal/components/onyx-base-form-control/onyx-base-form-control.component';
import { OnyxDotComponent } from '../../badges/onyx-dot/onyx-dot.component';
import { OnyxErrorComponent } from '../../error/onyx-error/onyx-error.component';
import { OnyxIconComponent } from '../../icons/onyx-icon/onyx-icon.component';
import { OnyxInputLabelComponent } from '../../labels/onyx-input-label/onyx-input-label.component';
import type { OnyxTooltipContext } from '../../tooltip/interfaces';
import type { OnyxTab } from '../interfaces';

@Component({
  selector: 'onyx-tabs',
  imports: [
    OnyxIconComponent,
    TranslatePipe,
    OnyxTooltipDirective,
    OnyxInputLabelComponent,
    OnyxErrorComponent,
    NgTemplateOutlet,
    OnyxDotComponent,
  ],
  templateUrl: './onyx-tabs.component.html',
  styleUrl: './onyx-tabs.component.scss',
  host: { '[style.width]': `width()` },
  providers: [
    {
      provide: ONYX_FOCUSABLE,
      useExisting: forwardRef(() => OnyxTabsComponent),
    },
  ],
})
export class OnyxTabsComponent<T>
  extends OnyxBaseFormControlComponent
  implements FormValueControl<OnyxOptional<T>>, AfterViewInit, OnInit
{
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly renderer = inject(Renderer2);

  public readonly value = model<T | null>();
  public readonly tabs = input.required<OnyxTab<T>[] | null | undefined>();
  public readonly label = input<string>();
  public readonly hint = input<OnyxTooltipContext>();
  public readonly templateRef = input<TemplateRef<any>>();
  public readonly optional = input(false);
  public readonly showRequired = input(true);
  public readonly showErrors = input(true);
  public readonly fill = input(false);
  public readonly size = input<32 | 40 | 64>(32);
  public readonly width = input('max-content');
  public readonly padding = input(12);

  protected activeTabIndex = signal<number | null>(null);

  private loadingValue = signal<T | null>(null);

  private containerElementRef =
    viewChild.required<ElementRef>('containerElement');
  private tabElementRefs = viewChildren<ElementRef>('tabElement');
  private indicatorElementRef =
    viewChild.required<ElementRef>('indicatorElement');

  public override ngOnInit(): void {
    super.ngOnInit();

    toObservable(this.value, { injector: this.injector })
      .pipe(
        filter((value) => value !== undefined),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((value) => this.transformValue(value));

    effect(
      () => {
        const tabs = this.tabs();
        if (tabs == null) return;

        const value = this.loadingValue();
        if (value != null) {
          this.transformValue(value);
          this.loadingValue.set(null);
        }
      },
      { injector: this.injector },
    );
  }

  public override ngAfterViewInit(): void {
    super.ngAfterViewInit();

    fromEventPattern<void>(
      (handler) => {
        const intersectionObserver = new IntersectionObserver(([entry]) => {
          if (entry.intersectionRatio === 1) handler();
        });
        intersectionObserver.observe(this.containerElementRef().nativeElement);

        return intersectionObserver;
      },
      (_, intersectionObserver: IntersectionObserver) => {
        intersectionObserver.disconnect();
      },
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.updateIndicator(true));

    fromEventPattern<void>(
      (handler) => {
        const resizeObserver = new ResizeObserver(() => handler());
        resizeObserver.observe(this.elementRef.nativeElement);

        return resizeObserver;
      },
      (_, resizeObserver?: ResizeObserver) => resizeObserver?.disconnect(),
    )
      .pipe(
        skip(1),
        takeUntilDestroyed(this.destroyRef),
        filter(() => this.fill()),
      )
      .subscribe(() => this.updateIndicator(true));
  }

  public focus(): void {
    this.tabElementRefs()[this.activeTabIndex() ?? 0]?.nativeElement.focus();
  }

  protected changeTab(index: number | null, force = false): void {
    const tabs = this.tabs();
    if (tabs == null) index = null;

    let value = null;
    if (tabs != null && index != null) {
      const tab = tabs[index];
      if (tab.disabled) return;

      value = tab.value;
    }

    if (!force && this.activeTabIndex() === index) {
      if (
        (!!(this.formField() ?? this.formControl()) && this.isOptional()) ||
        this.optional()
      ) {
        this.activeTabIndex.set(null);
        this.value.set(null);
      }

      setTimeout(() => this.updateIndicator());
      return;
    }

    let activeIndex: number | null = null;
    untracked(() => (activeIndex = this.activeTabIndex()));

    this.activeTabIndex.set(index);
    setTimeout(() => this.updateIndicator(activeIndex == null));

    if (!force) {
      this.value.set(value);
    }
  }

  private transformValue(value: T | null): void {
    const tabs = this.tabs();
    const index = tabs?.findIndex((tab) => tab.value === value);

    if (index === undefined || index === -1) {
      this.loadingValue.set(value);
      this.changeTab(null, true);
      return;
    }

    if (
      this.isOptional() &&
      index == null &&
      (this.activeTabIndex() === index ||
        (!this.activeTabIndex() && !this.value()))
    ) {
      return;
    }

    this.changeTab(index, true);
  }

  private updateIndicator(instant = false): void {
    const element = this.containerElementRef()?.nativeElement;
    const indicator = this.indicatorElementRef()?.nativeElement;
    const tabs = this.tabElementRefs();
    if (!element || !indicator || !tabs) return;

    const TRANSITION_DURATION = `${ONYX_TRANSITION_DURATION}ms`;
    if (instant) {
      this.renderer.setStyle(indicator, 'transition-duration', '0s');
      setTimeout(() =>
        this.renderer.setStyle(
          indicator,
          'transition-duration',
          TRANSITION_DURATION,
        ),
      );
    }

    const activeTabIndex = this.activeTabIndex();
    if (activeTabIndex != null) {
      const tab = tabs[activeTabIndex]?.nativeElement;
      if (!tab) return;

      this.renderer.setStyle(indicator, 'opacity', '1');
      this.renderer.setStyle(indicator, 'width', `${tab.clientWidth - 1}px`);
      this.renderer.setStyle(
        indicator,
        'transform',
        `translateX(${tab.offsetLeft - element.clientLeft - 1}px)`,
      );

      if (
        tab.offsetLeft < element.scrollLeft ||
        tab.offsetLeft + tab.clientWidth > element.offsetWidth
      ) {
        element.scroll({ left: tab.offsetLeft, behavior: 'smooth' });
      }
    } else {
      this.renderer.setStyle(indicator, 'opacity', '0');
    }

    if (!indicator.style.transitionDuration) {
      setTimeout(() =>
        this.renderer.setStyle(
          indicator,
          'transition-duration',
          TRANSITION_DURATION,
        ),
      );
    }
  }
}
